import type {getCallEvent_onCallEvent as CallEvent} from "./types/getCallEvent";
import type {getChannelCallState_onChannelCallStateChange as SwitchEvent} from "./types/getChannelCallState";

/**
 * A row in the activity list. Both PBXs produce this shape: FusionPBX sends it
 * straight from the switch, and an offhook instance sends the call-event
 * contract, which rowFromCallEvent turns into the same thing. The list and its
 * items therefore render one shape and know nothing about either stream.
 *
 * `held` and `sequence` are ours: the FreeSWITCH events carried neither.
 */
export type CallRow = Omit<SwitchEvent, "__typename"> & {
    held?: boolean;
    sequence?: number;
};

/**
 * The call-event contract as a row. `callDirection` is the leg direction
 * relative to the media server, which is the convention the FreeSWITCH events
 * used and the one the list's rendering already inverts.
 */
export function rowFromCallEvent(event: CallEvent): CallRow {
    return {
        event: event.event,
        channelId: event.callId,
        channelCallState: event.state.toUpperCase(),
        callDirection: event.leg.direction,
        channelPresenceId: event.leg.presenceId,
        callerIdName: event.caller.name,
        callerIdNumber: event.caller.number,
        calleeIdName: event.callee.name,
        calleeIdNumber: event.callee.number,
        callerDestinationNumber: event.destination,
        held: false,
        sequence: event.sequence,
    };
}

/**
 * Folds one call event into the rows on screen. Delivery is at least once and
 * unordered, so this keys on the call id, ignores an event older than the one
 * a row already holds, and can be given the same event twice.
 */
export function reduceCallEvent(rows: CallRow[], event: CallEvent): CallRow[] {
    // Parking is a state of a call the user is no longer on; the list shows
    // the calls at their own phone.
    if (event.event.startsWith("park.")) {
        return rows;
    }

    const current = rows.find((row) => row.channelId === event.callId);
    if (current && current.sequence !== undefined && event.sequence <= current.sequence) {
        return rows;
    }

    const others = rows.filter((row) => row.channelId !== event.callId);

    switch (event.state) {
        case "hangup":
            return others;
        case "ringing":
        case "active":
            // Ordered last so the newest call is at the bottom, as the
            // FreeSWITCH reducer has always done.
            return [...others, {...rowFromCallEvent(event), held: current?.held ?? false}];
        case "held":
        case "unheld":
            // A hold says nothing about a call we are not already showing.
            if (!current) {
                return rows;
            }
            return [...others, {...current, held: event.state === "held", sequence: event.sequence}];
        default:
            return rows;
    }
}
